// Mes emplacements de marché (GET /usager/attributions) : attribution, marché, emplacement,
// période, situation, redevances émises avec leur règle et le lien vers la créance à payer.
import { EtatVide } from '@cc/ui';
import { Store } from 'lucide-react';
import { EtatRequete } from '../../../commun/EtatRequete';
import { date } from '../../../commun/format';
import { EnTeteCitoyen, ExigeConnexion, Ligne, Liste, Section, TexteStatut } from '../commun';
import { useMesAttributions, useMesCreances } from './api';
import { aPayer, LIBELLES_SITUATION_ATTRIBUTION, montant } from './logique';
import type { AttributionUsager } from './types';
import styles from '../citoyen.module.css';

const TON: Record<string, 'ok' | 'info' | 'attention' | 'neutre'> = { en_cours: 'ok', a_venir: 'info', echue: 'neutre', resiliee: 'attention', terminee: 'neutre' };
const LIBELLES_PERIODICITE: Record<string, string> = { mensuelle: 'redevance mensuelle', trimestrielle: 'redevance trimestrielle' };

function Attribution({ a, creancesOuvertes }: { a: AttributionUsager; creancesOuvertes: Set<string> }) {
  return (
    <Section titre={`${a.marche?.nom ?? 'Marché'} · emplacement ${a.emplacement.reference ?? ''}`} id={`attribution-${a.id}`}>
      <p className={styles.aide}>
        <span className={styles.mono}>{a.numero}</span> · {a.collectivite.nom} · du {date(a.periode.debut)} au {date(a.periode.fin)} · {a.surface_m2.toLocaleString('fr-FR')} m² · {LIBELLES_PERIODICITE[a.periodicite] ?? a.periodicite} ·{' '}
        <TexteStatut libelle={LIBELLES_SITUATION_ATTRIBUTION[a.situation] ?? a.situation} ton={TON[a.situation] ?? 'neutre'} />
      </p>
      {a.resiliation ? <p className={styles.aide}>Résiliée à effet du {date(a.resiliation.date_effet)} : {a.resiliation.motif}</p> : null}
      {a.renouvellement_propose_le ? <p className={styles.aide}>Un renouvellement vous est proposé : rapprochez-vous du service des marchés.</p> : null}
      {a.redevances.length === 0 ? (
        <p className={styles.aide}>Aucune redevance émise pour l&apos;instant.</p>
      ) : (
        <Liste libelle={`Redevances de l'attribution ${a.numero}`}>
          {a.redevances.map((r) => (
            <Ligne
              key={r.rang}
              href={creancesOuvertes.has(r.creance.id) ? `/citoyen/paiements/creances/${r.creance.id}` : undefined}
              titre={`Période ${r.rang} : du ${date(r.periode.debut)} au ${date(r.periode.fin)}`}
              sousTitre={`${r.creance.numero} · ${r.creance.regle} · échéance le ${date(r.echeance)}`}
              droite={<span className={styles.mono}>{montant(r.creance.montant_du)}</span>}
            />
          ))}
        </Liste>
      )}
    </Section>
  );
}

export function PageMesAttributions() {
  const attributions = useMesAttributions();
  const creances = useMesCreances();
  const ouvertes = new Set((creances.data ?? []).filter(aPayer).map((c) => c.id));
  return (
    <>
      <EnTeteCitoyen titre="Mes emplacements de marché" retour="/citoyen/paiements" libelleRetour="Paiements" sousTitre="Vos attributions d'emplacement et les redevances qui en découlent." />
      <ExigeConnexion titre="Connectez-vous pour voir vos emplacements">
        <EtatRequete chargement={attributions.isPending} erreur={attributions.error} onReessayer={() => void attributions.refetch()} fonction="Vos attributions" lot="L2" lignes={4}>
          {(attributions.data ?? []).length === 0 ? (
            <EtatVide titre="Aucun emplacement attribué" icone={Store}>
              <p>Une demande d&apos;emplacement se dépose depuis le catalogue des démarches de votre commune ; l&apos;attribution apparaît ici une fois décidée.</p>
            </EtatVide>
          ) : (
            (attributions.data ?? []).map((a) => <Attribution key={a.id} a={a} creancesOuvertes={ouvertes} />)
          )}
        </EtatRequete>
      </ExigeConnexion>
    </>
  );
}
